import { useCallback } from "react";
import { gql } from "@apollo/client";
import {
  ActivityLoggerProvider,
  RouteTracker,
} from "@atomic/activity-logger/react";
import type { ActivityEvent } from "@atomic/activity-logger";
import { apolloClient } from "../services/backend_gateway/apollo-client";
import { useAuth } from "../hooks/useAuth";
import { COMPANY_CODE } from "../client_identity";

/**
 * Page-view logging for the LMNT app. Mount once inside the router and inside
 * AuthProvider (App.tsx, next to <Outlet/>). Batches are POSTed to this app's own
 * backend via the `logActivity` GraphQL mutation, which SigV4-forwards them to core's
 * central activity log (`/service/activity`); the browser never talks to core directly.
 */
const LOG_ACTIVITY = gql`
  mutation LogActivity($eventsJSON: String!) {
    logActivity(eventsJSON: $eventsJSON) {
      accepted
    }
  }
`;

export function ActivityLogging() {
  const { user } = useAuth();

  const send = useCallback(async (events: ActivityEvent[]) => {
    await apolloClient.mutate({
      mutation: LOG_ACTIVITY,
      variables: { eventsJSON: JSON.stringify(events) },
      fetchPolicy: "no-cache",
    });
  }, []);

  const getContext = useCallback(
    () => ({
      userId: user?.id ?? undefined,
      userEmail: user?.email ?? undefined,
    }),
    [user],
  );

  return (
    <ActivityLoggerProvider app={COMPANY_CODE} send={send} getContext={getContext}>
      <RouteTracker app={COMPANY_CODE} />
    </ActivityLoggerProvider>
  );
}
